/**
 * API Contracts — single source of truth for the request and response shape
 * of HTTP routes that are shared between the frontend and the backend.
 *
 * Each contract pairs:
 *   - method + path template (with `:param` placeholders)
 *   - optional Zod schemas for path params, query string, and request body
 *   - a Zod schema for the success response shape
 *
 * The contract-test harness (`tests/contracts.test.ts`) walks this module and
 * asserts every declared route's response parses cleanly. The frontend's
 * `apiFetch(contract, params)` helper in `client/src/lib/apiFetch.ts` uses the
 * same schemas to type and validate responses, so a schema change is a single
 * edit instead of two.
 *
 * To add a new route, see `docs/contracts.md`.
 */
import { z } from "zod";
import { insertBookingSchema, insertHealingJourneySchema } from "./schema";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiContract<
  TParams extends z.ZodTypeAny = z.ZodTypeAny,
  TQuery extends z.ZodTypeAny = z.ZodTypeAny,
  TBody extends z.ZodTypeAny = z.ZodTypeAny,
  TResponse extends z.ZodTypeAny = z.ZodTypeAny,
> {
  name: string;
  method: HttpMethod;
  /** Path template with `:name` placeholders, e.g. `/api/bookings/:artistSlug` */
  path: string;
  params?: TParams;
  query?: TQuery;
  body?: TBody;
  response: TResponse;
  /** When true, contract tests must send an `x-admin-key` header. */
  requiresAdmin?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const dateString = z.string();
const dateOrString = z.union([z.string(), z.date()]);
const nullableDateOrString = dateOrString.nullable();

// ── /api/bookings/:artistSlug — booking list (booked date+time pairs) ─────
export const bookingsByArtistContract = {
  name: "bookingsByArtist",
  method: "GET",
  path: "/api/bookings/:artistSlug",
  params: z.object({ artistSlug: z.string().min(1) }),
  response: z.array(
    z.object({
      date: z.string(),
      time: z.string(),
    }),
  ),
} as const satisfies ApiContract;

// ── /api/bookings/:artistSlug/:date — booked time slots for a given date ──
export const bookedTimesForDateContract = {
  name: "bookedTimesForDate",
  method: "GET",
  path: "/api/bookings/:artistSlug/:date",
  params: z.object({
    artistSlug: z.string().min(1),
    date: z.string().min(1),
  }),
  response: z.array(
    z.object({
      time: z.string(),
      estimatedHours: z.number().nullable(),
    }),
  ),
} as const satisfies ApiContract;

// ── POST /api/bookings — create booking ───────────────────────────────────
const bookingCreateBody = insertBookingSchema.extend({
  // Frontend may pass these extras alongside the insert schema
  "cf-turnstile-response": z.string().optional(),
  confirmDuplicate: z.boolean().optional(),
  acknowledge: z.boolean().optional(),
  sendClientEmail: z.boolean().optional(),
  subscribeToNewsletter: z.boolean().optional(),
});

// The booking-create endpoint can return one of three branches (success,
// limit-reached, duplicate-detected). All fields are optional at the contract
// level — the frontend narrows by checking which discriminator is present —
// but every field that exists must have the documented type, which is what
// keeps the frontend and backend in lockstep.
const bookingCreateResponse = z.object({
  success: z.literal(true).optional(),
  id: z.number().optional(),
  referenceCode: z.string().optional(),
  date: z.string().nullable().optional(),
  time: z.string().nullable().optional(),
  artistSlug: z.string().nullable().optional(),
  releaseFormToken: z.string().nullable().optional(),
  releaseFormOnFile: z.boolean().optional(),
  depositRequired: z.boolean().optional(),
  selfEstimateToken: z.string().nullable().optional(),
  limitReached: z.boolean().optional(),
  submitted: z.number().optional(),
  limit: z.number().optional(),
  studioEmail: z.string().nullable().optional(),
  studioPhone: z.string().nullable().optional(),
  studioName: z.string().optional(),
  potentialDuplicate: z.boolean().optional(),
  existingBooking: z
    .object({
      referenceCode: z.string(),
      date: z.string(),
      time: z.string(),
      artistSlug: z.string().nullable(),
      artistName: z.string(),
    })
    .optional(),
  requiresAcknowledgment: z.boolean().optional(),
  duplicateWarning: z.string().optional(),
  overbookingWarning: z.string().optional(),
  genericPaymentLink: z.string().nullable().optional(),
  referenceImageSignedUrls: z.array(z.string()).optional(),
  depositAmount: z.number().nullable().optional(),
});

export type BookingCreateResponse = z.infer<typeof bookingCreateResponse>;

export const bookingCreateContract = {
  name: "bookingCreate",
  method: "POST",
  path: "/api/bookings",
  body: bookingCreateBody,
  response: bookingCreateResponse,
} as const satisfies ApiContract;

// ── POST /api/healing-journeys — healing check-in (journey create) ────────
const healingJourneyCreateBody = insertHealingJourneySchema.extend({
  referenceCode: z.string().optional(),
  bookingId: z.number().nullable().optional(),
});

export const healingJourneyCreateContract = {
  name: "healingJourneyCreate",
  method: "POST",
  path: "/api/healing-journeys",
  body: healingJourneyCreateBody,
  response: z.object({
    id: z.number(),
    bookingId: z.number().nullable(),
    tattooDescription: z.string(),
    artist: z.string().nullable(),
    placement: z.string(),
    tattooDate: dateString,
    clientEmail: z.string().nullable(),
    clientName: z.string().nullable(),
    dripStep: z.number(),
    googleReviewEmailSentAt: nullableDateOrString,
    createdAt: dateOrString,
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/netglowup/leads — NGU leads list (admin) ───────────────
export const nguLeadsListContract = {
  name: "nguLeadsList",
  method: "GET",
  path: "/api/admin/netglowup/leads",
  requiresAdmin: true,
  response: z.object({
    total: z.number(),
    hasMore: z.boolean(),
    leads: z.array(
      z.object({
        id: z.number(),
        email: z.string(),
        shopName: z.string().nullable(),
        ownerName: z.string().nullable(),
        segment: z.string().nullable(),
        segmentLabel: z.string().nullable(),
        status: z.string().nullable(),
        pipelineStage: z.string().nullable(),
        pipelineStageEnteredAt: nullableDateOrString,
        qualificationScore: z.number().nullable(),
        dealValue: z.number(),
        dripStep: z.number(),
        waitlistJoinedAt: nullableDateOrString,
        fastTrackedAt: nullableDateOrString,
        profileViewedAt: nullableDateOrString,
        closedAt: nullableDateOrString,
        closedMonthlyValue: z.number().nullable(),
        closeNote: z.string().nullable(),
        specialties: z.array(z.string()),
        createdAt: dateOrString,
        painSurvey: z.object({
          painPointId: z.string(),
          painPointLabel: z.string(),
          featureMapping: z.string(),
          icpSlug: z.string(),
          otherText: z.string().nullable(),
          submittedAt: dateOrString,
        }).nullable(),
      }),
    ),
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/validate-token — admin "login" / token check ───────────
export const adminValidateTokenContract = {
  name: "adminValidateToken",
  method: "GET",
  path: "/api/admin/validate-token",
  requiresAdmin: true,
  response: z.object({
    valid: z.literal(true),
    scope: z.string().optional(),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Public read endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/system/flags — top-level deployment flags ────────────────────
export const systemFlagsContract = {
  name: "systemFlags",
  method: "GET",
  path: "/api/system/flags",
  response: z.object({
    isAgencyDeployment: z.boolean(),
    agencySiteUrl: z.string().nullable().optional(),
    nguClientRedirectEnabled: z.boolean().optional(),
  }),
} as const satisfies ApiContract;

// ── GET /api/build-info — current server build identity ───────────────────
export const buildInfoContract = {
  name: "buildInfo",
  method: "GET",
  path: "/api/build-info",
  response: z.object({
    buildId: z.string(),
    version: z.string(),
    date: z.string(),
  }),
} as const satisfies ApiContract;

// ── GET /api/feature-flags/enabled — public flag map ──────────────────────
export const featureFlagsEnabledContract = {
  name: "featureFlagsEnabled",
  method: "GET",
  path: "/api/feature-flags/enabled",
  query: z
    .object({
      deploymentId: z.union([z.string(), z.number()]).optional(),
    })
    .optional(),
  response: z.record(z.string(), z.boolean()),
} as const satisfies ApiContract;

// ── GET /api/feature-flags/meta — public static metadata for all flags ────
export const featureFlagsMetaContract = {
  name: "featureFlagsMeta",
  method: "GET",
  path: "/api/feature-flags/meta",
  response: z.array(z.object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    prodDefault: z.boolean(),
    tested: z.boolean(),
    mvpComplete: z.boolean(),
    category: z.string(),
  })),
} as const satisfies ApiContract;

// ── GET /api/piercing-pricing — public price list ─────────────────────────
// Mirrors the `piercing_pricing` table shape that AdminPiercingPricing.tsx and
// the public price list both consume. The public route is unauthenticated and
// returns the same rows the admin route does (no credential columns on this
// entity), so a single contract serves both consumers.
const piercingPricingRowSchema = z.object({
  id: z.number(),
  piercingType: z.string(),
  displayName: z.string(),
  priceMin: z.number(),
  priceMax: z.number(),
  jewelryUpcharge: z.number(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number(),
  isActive: z.boolean(),
  createdAt: dateOrString.nullable().optional(),
});

export const piercingPricingContract = {
  name: "piercingPricing",
  method: "GET",
  path: "/api/piercing-pricing",
  response: z.array(piercingPricingRowSchema),
} as const satisfies ApiContract;

// Body shape accepted by both the create and update routes — mirrors
// insertPiercingPricingSchema (server validates with the same shape).
const piercingPricingBodySchema = z.object({
  piercingType: z.string().min(1).max(100),
  displayName: z.string().min(1).max(200),
  priceMin: z.number().int().min(0),
  priceMax: z.number().int().min(0),
  jewelryUpcharge: z.number().int().min(0).optional(),
  notes: z.string().max(1000).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

// ── POST /api/piercing-pricing — create a price row ───────────────────────
export const piercingPricingCreateContract = {
  name: "piercingPricingCreate",
  method: "POST",
  path: "/api/piercing-pricing",
  requiresAdmin: true,
  body: piercingPricingBodySchema,
  response: piercingPricingRowSchema,
} as const satisfies ApiContract;

// ── PUT /api/piercing-pricing/:id — update a price row ────────────────────
export const piercingPricingUpdateContract = {
  name: "piercingPricingUpdate",
  method: "PUT",
  path: "/api/piercing-pricing/:id",
  requiresAdmin: true,
  params: z.object({ id: z.union([z.string(), z.number()]) }),
  body: piercingPricingBodySchema,
  response: piercingPricingRowSchema,
} as const satisfies ApiContract;

// ── DELETE /api/piercing-pricing/:id — remove a price row ─────────────────
export const piercingPricingDeleteContract = {
  name: "piercingPricingDelete",
  method: "DELETE",
  path: "/api/piercing-pricing/:id",
  requiresAdmin: true,
  params: z.object({ id: z.union([z.string(), z.number()]) }),
  response: z.object({ success: z.literal(true) }),
} as const satisfies ApiContract;

// ── GET /api/health — UptimeRobot-compatible lean health check ────────────
export const healthCheckContract = {
  name: "healthCheck",
  method: "GET",
  path: "/api/health",
  response: z
    .object({
      status: z.enum(["ok", "degraded", "down"]),
      timestamp: z.string(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── GET /api/manifest.json — PWA manifest ─────────────────────────────────
export const webManifestContract = {
  name: "webManifest",
  method: "GET",
  path: "/api/manifest.json",
  response: z
    .object({
      name: z.string(),
      short_name: z.string(),
      description: z.string(),
      start_url: z.string(),
      display: z.string(),
      icons: z.array(z.object({ src: z.string(), sizes: z.string(), type: z.string() }).passthrough()),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── Artist (public sanitized shape) — shared by /api/artists & /api/artists/all ──
const publicArtistSchema = z
  .object({
    id: z.number(),
    slug: z.string(),
    name: z.string(),
    role: z.string(),
    image: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    instagram: z.string().nullable().optional(),
    specialties: z.array(z.string()),
    accentColor: z.string().nullable().optional(),
    isActive: z.boolean(),
    sortOrder: z.number(),
  })
  .passthrough();

// ── GET /api/artists — public artist list with portfolio thumbs ───────────
export const artistsPublicContract = {
  name: "artistsPublic",
  method: "GET",
  path: "/api/artists",
  response: z.array(
    publicArtistSchema.extend({
      portfolio: z.array(
        z.object({
          id: z.number(),
          src: z.string(),
          alt: z.string(),
        }),
      ),
    }),
  ),
} as const satisfies ApiContract;

// ── GET /api/artists/all — admin list (sanitized minus IG token) ─────────
export const artistsAllContract = {
  name: "artistsAll",
  method: "GET",
  path: "/api/artists/all",
  requiresAdmin: true,
  response: z.array(publicArtistSchema),
} as const satisfies ApiContract;

// ── GET /api/portfolio-images — global portfolio image list ───────────────
export const portfolioImagesContract = {
  name: "portfolioImages",
  method: "GET",
  path: "/api/portfolio-images",
  response: z.array(
    z
      .object({
        id: z.number(),
        artistId: z.number(),
        src: z.string(),
        alt: z.string(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/site-config — public site/visual config ──────────────────────
// Mirrors the SiteConfig interface in client/src/lib/SiteConfigContext.tsx —
// every field the frontend reads off the response is enumerated here so a
// missing/renamed field in the API trips the contract test instead of a
// silent runtime undefined.
const siteConfigPosition = z.object({ x: z.number(), y: z.number() });
const siteConfigStudioImage = z.object({ src: z.string(), alt: z.string() }).passthrough();
export const siteConfigContract = {
  name: "siteConfig",
  method: "GET",
  path: "/api/site-config",
  response: z
    .object({
      heroOpacity: z.number().optional(),
      heroCardBgOpacity: z.number().optional(),
      heroVideoOpacity: z.number().optional(),
      heroWispsEnabled: z.boolean().optional(),
      heroTextShadow: z.number().optional(),
      italicText: z.boolean().optional(),
      tattooItalic: z.boolean().optional(),
      tileSize: z.number().optional(),
      tileOffsetX: z.number().optional(),
      tileOffsetY: z.number().optional(),
      magikGlowColor1: z.string().optional(),
      magikGlowColor2: z.string().optional(),
      magikGlowIntensity: z.number().optional(),
      magikGlowSpeed: z.number().optional(),
      textBgOpacity: z.number().optional(),
      artistCardScale: z.number().optional(),
      artistCardRadius: z.number().optional(),
      artistCardBorderWidth: z.number().optional(),
      artistCardBorderColor: z.string().optional(),
      artistCardFramePadding: z.number().optional(),
      artistCardFrameColor: z.string().optional(),
      artistCardVignetteOpacity: z.number().optional(),
      artistCardVignetteCenter: z.number().optional(),
      artistCardPositions: z.record(z.string(), siteConfigPosition).optional(),
      artistProfileZoom: z.record(z.string(), z.number()).optional(),
      artistHeroOrder: z.record(z.string(), z.array(z.string())).optional(),
      aboutHeroImages: z.array(z.string()).optional(),
      aboutStudioImages: z.array(siteConfigStudioImage).optional(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── GET /api/studio-config — public studio profile + booking window ──────
// Mirrors every field the use-studio-config hook reads so the contract type
// is a drop-in replacement for the hand-rolled StudioConfigData interface
// (no manual cast needed).
const lightroomShowcaseSchema = z.object({
  name: z.string(),
  embedUrl: z.string(),
  externalUrl: z.string().optional(),
});
const heroVideoPlaylistEntrySchema = z.object({
  url: z.string(),
  loopStart: z.number(),
  loopEnd: z.number().nullable(),
});
export const studioConfigContract = {
  name: "studioConfig",
  method: "GET",
  path: "/api/studio-config",
  response: z
    .object({
      name: z.string(),
      shortName: z.string(),
      brandSuffix: z.string().nullable().optional(),
      slogan: z.string().nullable().optional(),
      heroTagline: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      established: z.number().nullable().optional(),
      address: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
      zip: z.string().nullable().optional(),
      fullAddress: z.string().optional(),
      phone: z.string().nullable().optional(),
      contactEmail: z.string().nullable().optional(),
      siteUrl: z.string().nullable().optional(),
      heroImage: z.string().nullable().optional(),
      heroBlackBg: z.boolean().nullable().optional(),
      logoUrl: z.string().nullable().optional(),
      logoBlackBg: z.boolean().nullable().optional(),
      footerImageUrl: z.string().nullable().optional(),
      footerBlackBg: z.boolean().nullable().optional(),
      footerImageAdjustments: z.record(z.number()).nullable().optional(),
      heroImageAdjustments: z.record(z.number()).nullable().optional(),
      teamPhotoUrl: z.string().nullable().optional(),
      teamBlackBg: z.boolean().nullable().optional(),
      teamPhotoAdjustments: z.record(z.number()).nullable().optional(),
      hours: z.string().nullable().optional(),
      hoursNote: z.string().nullable().optional(),
      instagramUrl: z.string().nullable().optional(),
      facebookUrl: z.string().nullable().optional(),
      externalGalleryUrl: z.string().nullable().optional(),
      lat: z.string().nullable().optional(),
      lng: z.string().nullable().optional(),
      mapUrl: z.string().nullable().optional(),
      defaultDeposit: z.number().nullable().optional(),
      shopUrl: z.string().nullable().optional(),
      onboardingComplete: z.boolean().nullable().optional(),
      privacyPolicyUrl: z.string().nullable().optional(),
      bookingSlotStartHour: z.number(),
      bookingSlotEndHour: z.number(),
      bookingSlotIntervalMinutes: z.number(),
      lightroomShowcases: z.array(lightroomShowcaseSchema).nullable().optional(),
      heroVideoUrl: z.string().nullable().optional(),
      heroVideoLoopEnabled: z.boolean().nullable().optional(),
      heroVideoLoopStart: z.number().nullable().optional(),
      heroVideoLoopEnd: z.number().nullable().optional(),
      heroVideoPlaylist: z.array(heroVideoPlaylistEntrySchema).nullable().optional(),
      cancellationWindowHours: z.number().nullable().optional(),
      parkingNotes: z.string().nullable().optional(),
      backgroundTileUrl: z.string().nullable().optional(),
      /** Active deployment profile sourced from APP_PROFILE env var. Defaults to "client". */
      profile: z.enum(["agency", "client", "demo"]).optional(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── GET /api/availability/:artistSlug — booking window for one artist ─────
export const artistAvailabilityContract = {
  name: "artistAvailability",
  method: "GET",
  path: "/api/availability/:artistSlug",
  params: z.object({ artistSlug: z.string().min(1) }),
  response: z
    .object({
      artistSlug: z.string(),
      status: z.string(),
      nextAvailableDate: z.string().nullable(),
      minWeeksOut: z.number(),
      note: z.string().nullable().optional(),
      blockedDates: z.array(z.string()),
      recurringBlockedDays: z.array(z.union([z.number(), z.string()])).optional(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── GET /api/events — public published events ────────────────────────────
// Mirrors the `events` table shape consumed by Layout.tsx (upcoming events
// banner) and the public Events page. Kept loose on optional fields to avoid
// breakage when seed data is sparse, but the shape is concrete enough that
// the frontend can drop its own duplicate UpcomingEvent interface.
const eventSchema = z
  .object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullable().optional(),
    date: z.string().nullable().optional(),
    time: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    isPublished: z.boolean().nullable().optional(),
  })
  .passthrough();

export const eventsPublicContract = {
  name: "eventsPublic",
  method: "GET",
  path: "/api/events",
  response: z.array(eventSchema),
} as const satisfies ApiContract;

// ── GET /api/events/upcoming — public upcoming events ────────────────────
export const eventsUpcomingContract = {
  name: "eventsUpcoming",
  method: "GET",
  path: "/api/events/upcoming",
  response: z.array(eventSchema),
} as const satisfies ApiContract;

// ── GET /api/flash-designs — public flash list ───────────────────────────
export const flashDesignsAvailableContract = {
  name: "flashDesignsAvailable",
  method: "GET",
  path: "/api/flash-designs",
  response: z.array(
    z
      .object({
        id: z.number(),
        title: z.string().nullable().optional(),
        imageUrl: z.string().nullable().optional(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/netglowup/config — public NGU config ────────────────────────
export const nguPublicConfigContract = {
  name: "nguPublicConfig",
  method: "GET",
  path: "/api/netglowup/config",
  response: z.object({
    demoSchedulingUrl: z.string().nullable(),
    stripeEnabled: z.boolean(),
    stripePublishableKey: z.string().nullable(),
  }),
} as const satisfies ApiContract;

// ── GET /api/netglowup/tracking-config — public tracking IDs ─────────────
export const nguTrackingConfigContract = {
  name: "nguTrackingConfig",
  method: "GET",
  path: "/api/netglowup/tracking-config",
  response: z.object({
    metaPixelId: z.string().nullable(),
    gaMeasurementId: z.string().nullable(),
    googleAdsConversionId: z.string().nullable(),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Admin GET endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/admin/jobs/status — pg-boss queue health ────────────────────
export const adminJobsStatusContract = {
  name: "adminJobsStatus",
  method: "GET",
  path: "/api/admin/jobs/status",
  requiresAdmin: true,
  response: z
    .object({
      enabled: z.boolean(),
      mode: z.enum(["pgboss", "fallback", "test", "off"]),
      jobs: z.array(
        z
          .object({
            name: z.string(),
            description: z.string(),
            cron: z.string().nullable().optional(),
            runs: z.number(),
            failures: z.number(),
            inFlight: z.union([z.boolean(), z.number()]),
            lastSuccessAt: z.string().nullable(),
            lastFailureAt: z.string().nullable(),
            lastError: z.string().nullable(),
            lastDurationMs: z.number().nullable(),
            queueDepth: z.number(),
            failedDepth: z.number(),
            lastFailureOutput: z.string().nullable().optional(),
            priority: z.enum(["CRITICAL", "STANDARD", "BACKGROUND"]).optional(),
          })
          .passthrough(),
      ),
      totalQueueDepth: z.number(),
      totalFailedDepth: z.number(),
      failures24h: z.number(),
      poolHistory: z
        .array(
          z.object({
            t: z.number(),
            totalCount: z.number(),
            idleCount: z.number(),
            waitingCount: z.number(),
          }),
        )
        .optional(),
      lastBackupRan: z.string().nullable().optional(),
      lastRestoreDrill: z
        .object({
          at: z.string(),
          success: z.boolean(),
          backupFilename: z.string().nullable().optional(),
          durationMs: z.number().optional(),
          checksPassed: z.number().optional(),
          checksTotal: z.number().optional(),
          mode: z.string().optional(),
          error: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ── GET /api/admin/audit-logs — recent admin actions ─────────────────────
export const adminAuditLogsContract = {
  name: "adminAuditLogs",
  method: "GET",
  path: "/api/admin/audit-logs",
  requiresAdmin: true,
  query: z
    .object({
      entity: z.string().optional(),
      limit: z.union([z.string(), z.number()]).optional(),
      since: z.string().optional(),
    })
    .optional(),
  response: z.array(
    z
      .object({
        id: z.number(),
        action: z.string(),
        entity: z.string().nullable().optional(),
        entityId: z.union([z.number(), z.string()]).nullable().optional(),
        entityLabel: z.string().nullable().optional(),
        createdAt: dateOrString,
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/admin/changelog — release notes feed ────────────────────────
const changelogEntrySchema = z
  .object({
    hash: z.string(),
    shortHash: z.string(),
    date: z.string(),
    subject: z.string(),
    category: z.enum(["feature", "fix", "improvement", "maintenance"]),
    categoryLabel: z.string(),
    color: z.string(),
  })
  .passthrough();
export const adminChangelogContract = {
  name: "adminChangelog",
  method: "GET",
  path: "/api/admin/changelog",
  requiresAdmin: true,
  response: z.object({
    entries: z.array(changelogEntrySchema),
    build: z
      .object({
        version: z.string(),
        buildDate: z.string(),
        gitHead: z.string(),
        gitHeadShort: z.string(),
      })
      .passthrough(),
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/bookings/deleted — soft-deleted bookings ──────────────
// Frontend's DeletedBooking interface (AdminDeletedBookings.tsx) reads these
// columns; everything else stays passthrough so we don't have to mirror every
// nullable column on the bookings table.
const deletedBookingSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    artistSlug: z.string().nullable().optional(),
    date: z.string().nullable().optional(),
    time: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    referenceCode: z.string().nullable().optional(),
    deletedAt: dateOrString.nullable().optional(),
    estimatedCostMin: z.number().nullable().optional(),
    estimatedCostMax: z.number().nullable().optional(),
    depositStatus: z.string().nullable().optional(),
    serviceType: z.string().nullable().optional(),
    tattooSize: z.string().nullable().optional(),
    placement: z.string().nullable().optional(),
    piercingType: z.string().nullable().optional(),
    piercingCount: z.number().nullable().optional(),
  })
  .passthrough();
export const adminDeletedBookingsContract = {
  name: "adminDeletedBookings",
  method: "GET",
  path: "/api/admin/bookings/deleted",
  requiresAdmin: true,
  query: z.object({ days: z.union([z.string(), z.number()]).optional() }).optional(),
  response: z.object({
    bookings: z.array(deletedBookingSchema),
    days: z.number(),
  }),
} as const satisfies ApiContract;

// ── POST /api/admin/bookings/:id/restore — un-soft-delete a booking ──────
export const bookingRestoreContract = {
  name: "bookingRestore",
  method: "POST",
  path: "/api/admin/bookings/:id/restore",
  requiresAdmin: true,
  params: z.object({ id: z.union([z.string(), z.number()]) }),
  response: z.object({
    success: z.literal(true),
    booking: deletedBookingSchema,
  }),
} as const satisfies ApiContract;

// ── GET /api/feature-flags — admin flag list (with metadata) ─────────────
export const featureFlagsListContract = {
  name: "featureFlagsList",
  method: "GET",
  path: "/api/feature-flags",
  requiresAdmin: true,
  response: z.array(
    z
      .object({
        id: z.number(),
        key: z.string(),
        name: z.string(),
        description: z.string().nullable().optional(),
        enabled: z.boolean(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/feature-tiers — paid tier definitions ───────────────────────
export const featureTiersListContract = {
  name: "featureTiersList",
  method: "GET",
  path: "/api/feature-tiers",
  requiresAdmin: true,
  response: z.array(
    z
      .object({
        id: z.number(),
        name: z.string(),
        description: z.string().nullable().optional(),
        color: z.string().nullable().optional(),
        sortOrder: z.number().nullable().optional(),
        createdAt: dateOrString.nullable().optional(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/client-deployments — deployments registry ───────────────────
export const clientDeploymentsListContract = {
  name: "clientDeploymentsList",
  method: "GET",
  path: "/api/client-deployments",
  requiresAdmin: true,
  query: z.object({ tierId: z.union([z.string(), z.number()]).optional() }).optional(),
  response: z.array(
    z
      .object({
        id: z.number(),
        name: z.string(),
        url: z.string(),
        environment: z.string(),
        notes: z.string().nullable().optional(),
        active: z.boolean(),
        tierId: z.number().nullable().optional(),
        createdAt: dateOrString.nullable().optional(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/admin/activity/unread-count — activity badge counter ────────
export const adminActivityUnreadContract = {
  name: "adminActivityUnread",
  method: "GET",
  path: "/api/admin/activity/unread-count",
  requiresAdmin: true,
  query: z.object({ scope: z.enum(["studio", "ngu"]).optional() }).optional(),
  response: z.object({ count: z.number() }),
} as const satisfies ApiContract;

// ── GET /api/admin/activity/feed — paginated activity feed ───────────────
export const adminActivityFeedContract = {
  name: "adminActivityFeed",
  method: "GET",
  path: "/api/admin/activity/feed",
  requiresAdmin: true,
  query: z
    .object({
      limit: z.union([z.string(), z.number()]).optional(),
      offset: z.union([z.string(), z.number()]).optional(),
      scope: z.enum(["studio", "ngu"]).optional(),
    })
    .optional(),
  response: z
    .object({
      events: z.array(z.unknown()),
      announcements: z.array(z.unknown()),
      total: z.number(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                  Admin booking + contact + deposit endpoints
// ─────────────────────────────────────────────────────────────────────────

const bookingRowSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    artistSlug: z.string(),
    artistName: z.string(),
    date: z.string(),
    time: z.string(),
    serviceType: z.string(),
    status: z.string(),
    showStatus: z.string(),
    artistConfirmed: z.string(),
    depositStatus: z.string(),
    depositAmount: z.number().nullable().optional(),
    actualCost: z.number().nullable().optional(),
    referenceCode: z.string(),
    createdAt: dateOrString.nullable().optional(),
    completedAt: dateOrString.nullable().optional(),
    description: z.string().nullable().optional(),
    manageUrl: z.string().nullable().optional(),
    recoveredFromDraft: z.boolean().optional(),
    recoveredFromDraftAt: dateOrString.nullable().optional(),
    occasionTag: z.string().nullable().optional(),
  })
  .passthrough();

// ── GET /api/admin/all-bookings — paginated full booking list ─────────────
export const adminAllBookingsContract = {
  name: "adminAllBookings",
  method: "GET",
  path: "/api/admin/all-bookings",
  requiresAdmin: true,
  query: z
    .object({
      page: z.union([z.string(), z.number()]).optional(),
      limit: z.union([z.string(), z.number()]).optional(),
      sort: z.string().optional(),
      dir: z.enum(["asc", "desc"]).optional(),
      status: z.string().optional(),
      artistSlug: z.string().optional(),
      artist: z.string().optional(),
      showStatus: z.string().optional(),
      from: z.string().optional(),
      to: z.string().optional(),
      occasion: z.string().optional(),
      q: z.string().optional(),
    })
    .optional(),
  response: z.object({
    bookings: z.array(bookingRowSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/upcoming-bookings — near-future bookings list ──────────
const upcomingBookingSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    artistSlug: z.string(),
    artistName: z.string(),
    date: z.string(),
    time: z.string(),
    serviceType: z.string(),
    description: z.string().nullable().optional(),
    depositStatus: z.string(),
    depositAmount: z.number().nullable().optional(),
    artistConfirmed: z.string(),
    showStatus: z.string(),
    referenceCode: z.string(),
    manageUrl: z.string().nullable().optional(),
    piercingType: z.string().nullable().optional(),
    piercingCount: z.number().nullable().optional(),
  })
  .passthrough();

export const adminUpcomingBookingsContract = {
  name: "adminUpcomingBookings",
  method: "GET",
  path: "/api/admin/upcoming-bookings",
  requiresAdmin: true,
  response: z.object({
    bookings: z.array(upcomingBookingSchema),
    total: z.number(),
    generatedAt: z.string(),
    doubleBookings: z.array(
      z.object({
        key: z.string(),
        artistSlug: z.string(),
        artistName: z.string(),
        date: z.string(),
        time: z.string(),
        bookings: z.array(
          z.object({
            id: z.number(),
            name: z.string(),
            referenceCode: z.string(),
            manageUrl: z.string().nullable().optional(),
          }),
        ),
      }),
    ),
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/contact-submissions — inbound contact form leads ───────
const contactSubmissionSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    artist: z.string().nullable().optional(),
    serviceType: z.string(),
    message: z.string(),
    source: z.string().nullable().optional(),
    utmSource: z.string().nullable().optional(),
    utmMedium: z.string().nullable().optional(),
    utmCampaign: z.string().nullable().optional(),
    createdAt: z.string(),
    respondedAt: dateOrString.nullable().optional(),
  })
  .passthrough();

export const adminContactSubmissionsContract = {
  name: "adminContactSubmissions",
  method: "GET",
  path: "/api/admin/contact-submissions",
  requiresAdmin: true,
  response: z.array(contactSubmissionSchema),
} as const satisfies ApiContract;

// ── GET /api/admin/contacts — imported contacts list ─────────────────────
const importedContactSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    tags: z.array(z.string()),
    subscribedToList: z.boolean(),
    profilePhotoUrl: z.string().nullable().optional(),
    imageGallery: z
      .array(
        z.object({
          url: z.string(),
          caption: z.string().nullable().optional(),
          uploadedAt: z.string(),
        }),
      )
      .optional(),
    importedAt: dateOrString,
    sessionCount: z.number().optional(),
    linkedSessionCount: z.number().optional(),
    latestBookingDate: z.string().nullable().optional(),
  })
  .passthrough();

export const adminContactsContract = {
  name: "adminContacts",
  method: "GET",
  path: "/api/admin/contacts",
  requiresAdmin: true,
  response: z.union([z.array(importedContactSchema), z.object({ items: z.array(importedContactSchema) }).passthrough()]),
} as const satisfies ApiContract;

// ── GET /api/admin/deposits — deposits KPI + records ─────────────────────
const depositRecordSchema = z
  .object({
    id: z.number(),
    referenceCode: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    artistSlug: z.string().nullable().optional(),
    date: z.string().nullable().optional(),
    serviceType: z.string().nullable().optional(),
    depositStatus: z.string(),
    depositAmount: z.number().nullable().optional(),
    depositPaidAt: dateOrString.nullable().optional(),
    depositRequestedAt: dateOrString.nullable().optional(),
    depositReceiptUrl: z.string().nullable().optional(),
    depositNote: z.string().nullable().optional(),
    paymentMethod: z.string(),
    bookingStatus: z.string().optional(),
    showStatus: z.string().optional(),
  })
  .passthrough();

export const adminDepositsContract = {
  name: "adminDeposits",
  method: "GET",
  path: "/api/admin/deposits",
  requiresAdmin: true,
  response: z.object({
    kpis: z
      .object({
        totalCollected: z.number(),
        paidCount: z.number(),
        pendingCount: z.number(),
        waivedCount: z.number(),
        forfeitedCount: z.number().optional(),
        forfeitedTotal: z.number().optional(),
        avgDeposit: z.number(),
        collectionRate: z.number(),
        byMethod: z
          .object({
            stripe: z.number(),
            paypal: z.number(),
            link: z.number(),
            manual: z.number(),
          })
          .passthrough(),
      })
      .passthrough(),
    deposits: z.array(depositRecordSchema),
  }),
} as const satisfies ApiContract;

// ── GET /api/admin/deposits/needs-attention — overdue/flagged deposits ────
const attentionRecordSchema = z
  .object({
    id: z.number(),
    referenceCode: z.string(),
    name: z.string(),
    email: z.string(),
    artistSlug: z.string().nullable().optional(),
    date: z.string().nullable().optional(),
    depositAmount: z.number().nullable().optional(),
    depositStatus: z.string(),
    artistConfirmed: z.string(),
    showStatus: z.string(),
  })
  .passthrough();

export const adminDepositsAttentionContract = {
  name: "adminDepositsAttention",
  method: "GET",
  path: "/api/admin/deposits/needs-attention",
  requiresAdmin: true,
  response: z.array(attentionRecordSchema),
} as const satisfies ApiContract;

// ── GET /api/admin/blocked-emails — email suppression list ───────────────
export const adminBlockedEmailsContract = {
  name: "adminBlockedEmails",
  method: "GET",
  path: "/api/admin/blocked-emails",
  requiresAdmin: true,
  response: z.array(
    z
      .object({
        id: z.number(),
        email: z.string(),
        reason: z.string().nullable().optional(),
        blockedAt: dateOrString,
        unblockedAt: dateOrString.nullable().optional(),
      })
      .passthrough(),
  ),
} as const satisfies ApiContract;

// ── GET /api/bookings/edit-details/:token — public edit-details page ──────
export const bookingEditDetailsContract = {
  name: "bookingEditDetails",
  method: "GET",
  path: "/api/bookings/edit-details/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z
    .object({
      id: z.number(),
      name: z.string(),
      referenceCode: z.string(),
      artistName: z.string(),
      dateFormatted: z.string(),
      timeFormatted: z.string(),
      serviceType: z.string(),
      description: z.string().nullable().optional(),
      tattooSize: z.string().nullable().optional(),
      tattooSizeInches: z.string().nullable().optional(),
      complexity: z.string().nullable().optional(),
      color: z.string().nullable().optional(),
      placement: z.string().nullable().optional(),
      placementNotes: z.string().nullable().optional(),
      estimatedCostMin: z.number().nullable().optional(),
      estimatedCostMax: z.number().nullable().optional(),
      estimatedHours: z.number().nullable().optional(),
      status: z.string(),
    })
    .passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
// Artist Portal — token-gated routes used by client/src/pages/ArtistPortal.tsx
// Token routes return 401 in tests (mock storage returns no artist), so the
// success-shape is locked in at the contract level for runtime use; the test
// gate accepts 401 without schema-validating the body. See tests/contracts.ts.
// ─────────────────────────────────────────────────────────────────────────
const portalTokenParams = z.object({ token: z.string().min(1) });

export const artistPortalInfoContract = {
  name: "artistPortalInfo",
  method: "GET",
  path: "/api/artist-portal/:token",
  params: portalTokenParams,
  response: z.object({
    slug: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    image: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
    instagram: z.string().nullable().optional(),
    specialties: z.array(z.string()).optional(),
    accentColor: z.string().nullable(),
    msgNotifyIntervalHours: z.number().nullable(),
    notifyOnNewBooking: z.boolean(),
    notifyOnCloseout: z.boolean(),
    notifyOnDailyDigest: z.boolean(),
  }).passthrough(),
} as const satisfies ApiContract;

const portalBookingItem = z.object({
  id: z.number(),
  referenceCode: z.string().nullable(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  date: z.string().nullable(),
  time: z.string().nullable(),
  serviceType: z.string().nullable(),
  artistConfirmed: z.string(),
  depositStatus: z.string(),
  depositAmount: z.number().nullable(),
  showStatus: z.string().nullable(),
  description: z.string().nullable(),
  manageToken: z.string().nullable(),
  releaseFormStatus: z.string().nullable(),
  referenceImageUrls: z.array(z.string()),
  chatImageUrls: z.array(z.string()).optional(),
  afterPhotoUrls: z.array(z.string()),
  bodyMapX: z.number().nullable(),
  bodyMapY: z.number().nullable(),
  bodyMapView: z.string().nullable(),
  bodyMapRadius: z.number().nullable(),
  healedPhotoEmailSentAt: z.string().nullable(),
  rescheduleReason: z.string().nullable(),
  unreadClientMessages: z.number(),
  piercingType: z.string().nullable(),
  jewelryMaterial: z.string().nullable(),
  jewelryGauge: z.string().nullable(),
  jewelryStyle: z.string().nullable(),
  piercingCount: z.number().nullable(),
  tipAmountCents: z.number().nullable().optional(),
  tipPaidAt: z.string().nullable().optional(),
}).passthrough();

export const artistPortalBookingsContract = {
  name: "artistPortalBookings",
  method: "GET",
  path: "/api/artist-portal/:token/bookings",
  params: portalTokenParams,
  response: z.array(portalBookingItem),
} as const satisfies ApiContract;

const portalClientItem = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  sessionCount: z.number(),
  lastBookingDate: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
}).passthrough();

export const artistPortalClientsContract = {
  name: "artistPortalClients",
  method: "GET",
  path: "/api/artist-portal/:token/clients",
  params: portalTokenParams,
  response: z.array(portalClientItem),
} as const satisfies ApiContract;

export const artistPortalEarningsContract = {
  name: "artistPortalEarnings",
  method: "GET",
  path: "/api/artist-portal/:token/earnings",
  params: portalTokenParams,
  response: z.object({
    year: z.number(),
    ytdTotal: z.number().nullable().optional(),
    ytdTips: z.number(),
    ytdSessions: z.number(),
    commissionEnabled: z.boolean().optional(),
    commissionType: z.string().nullable().optional(),
    commissionRate: z.number().nullable().optional(),
    boothRentalAmount: z.number().nullable().optional(),
    boothRentalPeriod: z.string().nullable().optional(),
    records: z.array(z.object({
      id: z.number(),
      bookingDate: z.string(),
      clientName: z.string(),
      serviceType: z.string().nullable(),
      sessionAmount: z.number(),
      tipAmount: z.number().nullable(),
      portalTipAmountCents: z.number().nullable().optional(),
      artistPayout: z.number(),
      studioTake: z.number(),
      commissionType: z.string(),
      commissionRate: z.number().nullable(),
      calculatedAt: z.string(),
    }).passthrough()),
  }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalWalkinsContract = {
  name: "artistPortalWalkins",
  method: "GET",
  path: "/api/artist-portal/:token/walkins",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalFlashContract = {
  name: "artistPortalFlash",
  method: "GET",
  path: "/api/artist-portal/:token/flash",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number(), title: z.string() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalWaitlistContract = {
  name: "artistPortalWaitlist",
  method: "GET",
  path: "/api/artist-portal/:token/waitlist",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalDepositsContract = {
  name: "artistPortalDeposits",
  method: "GET",
  path: "/api/artist-portal/:token/deposits",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number(), name: z.string(), email: z.string() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalFormsContract = {
  name: "artistPortalForms",
  method: "GET",
  path: "/api/artist-portal/:token/forms",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number(), name: z.string(), email: z.string() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalHealingContract = {
  name: "artistPortalHealing",
  method: "GET",
  path: "/api/artist-portal/:token/healing",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalStatsContract = {
  name: "artistPortalStats",
  method: "GET",
  path: "/api/artist-portal/:token/stats",
  params: portalTokenParams,
  response: z.object({
    stats: z.unknown().nullable(),
    totalClients: z.number(),
    repeatClients: z.number(),
    repeatRate: z.number(),
    waitlistCount: z.number(),
    healingJourneyCount: z.number(),
  }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalReviewsContract = {
  name: "artistPortalReviews",
  method: "GET",
  path: "/api/artist-portal/:token/reviews",
  params: portalTokenParams,
  response: z.object({
    avgScore: z.number().nullable(),
    count: z.number(),
    monthlyTrend: z.array(z.object({
      month: z.string(),
      avgScore: z.number(),
      count: z.number(),
    })),
    recentComments: z.array(z.object({
      score: z.number(),
      firstName: z.string(),
      comment: z.string(),
      createdAt: z.string(),
    })),
  }),
} as const satisfies ApiContract;

export const artistPortalTattooWorksContract = {
  name: "artistPortalTattooWorks",
  method: "GET",
  path: "/api/artist-portal/:token/tattoo-works",
  params: portalTokenParams,
  response: z.array(z.object({ id: z.number() }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalSupportTicketsContract = {
  name: "artistPortalSupportTickets",
  method: "GET",
  path: "/api/artist-portal/:token/support-tickets",
  params: portalTokenParams,
  response: z.array(z.object({
    id: z.number(),
    ticketNumber: z.number().nullable().optional(),
    title: z.string(),
    status: z.string(),
    impact: z.string().nullable().optional(),
    urgency: z.string().nullable().optional(),
    slaTier: z.string().nullable().optional(),
    createdAt: dateOrString,
    updatedAt: dateOrString.nullable().optional(),
  }).passthrough()),
} as const satisfies ApiContract;

export const artistPortalAvailabilityContract = {
  name: "artistPortalAvailability",
  method: "GET",
  path: "/api/artist-portal/:token/availability",
  params: portalTokenParams,
  response: z.object({
    status: z.string(),
    nextAvailableDate: z.string().nullable(),
    minWeeksOut: z.number(),
    note: z.string(),
    recurringBlockedDays: z.array(z.number()),
    blockedDates: z.array(z.string()),
  }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalProfileDataContract = {
  name: "artistPortalProfileData",
  method: "GET",
  path: "/api/artist-portal/:token/profile-data",
  params: portalTokenParams,
  response: z.object({
    id: z.number(),
    bio: z.string().nullable(),
    role: z.string(),
    instagram: z.string().nullable(),
    specialties: z.array(z.string()),
    accentColor: z.string(),
    tattooingSince: z.number().nullable(),
    tattooingSinceMonth: z.number().nullable(),
    image: z.string().nullable(),
    heroImages: z.array(z.unknown()),
  }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalFlashDeleteContract = {
  name: "artistPortalFlashDelete",
  method: "DELETE",
  path: "/api/artist-portal/:token/flash/:id",
  params: portalTokenParams.extend({ id: z.union([z.string(), z.number()]) }),
  response: z.object({ success: z.boolean() }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalFlashReorderContract = {
  name: "artistPortalFlashReorder",
  method: "POST",
  path: "/api/artist-portal/:token/flash/reorder",
  params: portalTokenParams,
  body: z.object({ orderedIds: z.array(z.number()) }),
  response: z.object({ success: z.boolean() }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalWaitlistNotifyContract = {
  name: "artistPortalWaitlistNotify",
  method: "POST",
  path: "/api/artist-portal/:token/waitlist/:id/notify",
  params: portalTokenParams.extend({ id: z.union([z.string(), z.number()]) }),
  response: z.object({ success: z.boolean() }).passthrough(),
} as const satisfies ApiContract;

export const artistPortalIssueExchangeCodeContract = {
  name: "artistPortalIssueExchangeCode",
  method: "POST",
  path: "/api/artist-portal/issue-exchange-code",
  response: z.object({ code: z.string() }).passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
// Staff (Receptionist) Portal — token-gated routes
// ─────────────────────────────────────────────────────────────────────────
export const staffPortalMeContract = {
  name: "staffPortalMe",
  method: "GET",
  path: "/api/staff/portal/:token/me",
  params: portalTokenParams,
  response: z.object({
    id: z.number(),
    name: z.string(),
    role: z.string().nullable().optional(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalUpcomingBookingsContract = {
  name: "staffPortalUpcomingBookings",
  method: "GET",
  path: "/api/staff/portal/:token/upcoming-bookings",
  params: portalTokenParams,
  response: z.object({
    bookings: z.array(z.object({
      id: z.number(),
      name: z.string(),
      email: z.string(),
      phone: z.string().nullable(),
      artistSlug: z.string(),
      artistName: z.string(),
      date: z.string(),
      time: z.string(),
      serviceType: z.string(),
      description: z.string().nullable(),
      tattooSize: z.string().nullable(),
      placement: z.string().nullable(),
      complexity: z.string().nullable(),
      color: z.string().nullable(),
      designStyle: z.string().nullable(),
      artistConfirmed: z.string(),
      releaseFormStatus: z.string().nullable(),
      referenceCode: z.string(),
      manageUrl: z.string().nullable(),
      piercingType: z.string().nullable(),
      jewelryMaterial: z.string().nullable(),
      jewelryGauge: z.string().nullable(),
      jewelryStyle: z.string().nullable(),
      piercingCount: z.number().nullable(),
      arrivalStatus: z.enum(["waiting", "in_chair", "done"]),
    }).passthrough()),
    total: z.number(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalWalkinQueueContract = {
  name: "staffPortalWalkinQueue",
  method: "GET",
  path: "/api/staff/portal/:token/walkin-queue",
  params: portalTokenParams,
  query: z.object({ date: z.string().optional() }).optional(),
  response: z.object({
    date: z.string(),
    requests: z.array(z.object({ id: z.number() }).passthrough()),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalMessagesSummaryContract = {
  name: "staffPortalMessagesSummary",
  method: "GET",
  path: "/api/staff/portal/:token/messages",
  params: portalTokenParams,
  response: z.object({
    bookings: z.array(z.object({
      bookingId: z.number(),
      clientName: z.string(),
      artistName: z.string(),
      date: z.string(),
      time: z.string().nullable().optional(),
      unreadCount: z.number(),
    }).passthrough()),
    totalUnread: z.number(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalMessageThreadContract = {
  name: "staffPortalMessageThread",
  method: "GET",
  path: "/api/staff/portal/:token/messages/:bookingId",
  params: portalTokenParams.extend({ bookingId: z.union([z.string(), z.number()]) }),
  response: z.object({
    messages: z.array(z.object({ id: z.number() }).passthrough()),
    clientName: z.string(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalMessageSendContract = {
  name: "staffPortalMessageSend",
  method: "POST",
  path: "/api/staff/portal/:token/messages/:bookingId",
  params: portalTokenParams.extend({ bookingId: z.union([z.string(), z.number()]) }),
  body: z.object({ content: z.string().min(1).max(1000) }),
  response: z.object({
    message: z.object({ id: z.number() }).passthrough(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalSupportTicketsContract = {
  name: "staffPortalSupportTickets",
  method: "GET",
  path: "/api/staff/portal/:token/support-tickets",
  params: portalTokenParams,
  response: z.array(z.object({
    id: z.number(),
    ticketNumber: z.number().nullable().optional(),
    title: z.string(),
    status: z.string(),
  }).passthrough()),
} as const satisfies ApiContract;

export const staffPortalTrainingProgressContract = {
  name: "staffPortalTrainingProgress",
  method: "GET",
  path: "/api/staff/portal/:token/training-progress",
  params: portalTokenParams,
  response: z.object({ completedModules: z.array(z.string()) }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalContactsContract = {
  name: "staffPortalContacts",
  method: "GET",
  path: "/api/staff/portal/:token/contacts",
  params: portalTokenParams,
  query: z.object({ search: z.string().optional() }).optional(),
  response: z.object({
    items: z.array(z.object({ id: z.number(), name: z.string().nullable(), email: z.string().nullable() }).passthrough()),
    total: z.number(),
  }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalNotificationsFeedContract = {
  name: "staffPortalNotificationsFeed",
  method: "GET",
  path: "/api/staff/portal/:token/notifications/feed",
  params: portalTokenParams,
  response: z.object({ events: z.array(z.unknown()) }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalArrivalStatusContract = {
  name: "staffPortalArrivalStatus",
  method: "PATCH",
  path: "/api/staff/portal/:token/bookings/:id/arrival-status",
  params: portalTokenParams.extend({ id: z.union([z.string(), z.number()]) }),
  body: z.object({ status: z.enum(["waiting", "in_chair", "done"]) }),
  response: z.object({ success: z.boolean().optional() }).passthrough(),
} as const satisfies ApiContract;

export const staffPortalWalkinUpdateContract = {
  name: "staffPortalWalkinUpdate",
  method: "PATCH",
  path: "/api/staff/portal/:token/walkin-queue/:id",
  params: portalTokenParams.extend({ id: z.union([z.string(), z.number()]) }),
  body: z.object({ action: z.enum(["approve", "decline"]), artistSlug: z.string().optional() }),
  response: z.object({
    success: z.boolean(),
    action: z.string(),
    bookingId: z.number().optional(),
    referenceCode: z.string().optional(),
    manageToken: z.string().optional(),
  }).passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
// Manage Studio — admin/studio token-gated routes (used by ManageStudio.tsx)
// ─────────────────────────────────────────────────────────────────────────
export const adminStudioConfigContract = {
  name: "adminStudioConfig",
  method: "GET",
  path: "/api/admin/studio/:token",
  params: portalTokenParams,
  response: z.object({}).passthrough(),
} as const satisfies ApiContract;

const commTemplateSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string(),
  subject: z.string(),
  body: z.string(),
  isDefault: z.boolean(),
  sortOrder: z.number(),
}).passthrough();

export const adminStudioCommTemplatesContract = {
  name: "adminStudioCommTemplates",
  method: "GET",
  path: "/api/admin/studio/:token/comm-templates",
  params: portalTokenParams,
  response: z.array(commTemplateSchema),
} as const satisfies ApiContract;

export const adminStudioCommTemplateDeleteContract = {
  name: "adminStudioCommTemplateDelete",
  method: "DELETE",
  path: "/api/admin/studio/:token/comm-templates/:id",
  params: portalTokenParams.extend({ id: z.union([z.string(), z.number()]) }),
  response: z.object({ success: z.boolean() }).passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Support portal endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/support/tickets — owner ticket list ──────────────────────────
// Auth: X-Admin-Token header (owner/digest token, min 32 chars).
// Returns the raw SupportTicket rows the owner can see.
const supportTicketRowSchema = z.object({
  id: z.number(),
  ticketNumber: z.number(),
  title: z.string(),
  description: z.string(),
  imageUrls: z.array(z.string()).optional(),
  status: z.string(),
  impact: z.string(),
  urgency: z.string(),
  slaTier: z.string(),
  eta: z.string().nullable().optional(),
  ownerEmail: z.string().nullable().optional(),
  studioName: z.string().nullable().optional(),
  submitterRole: z.string().nullable().optional(),
  submitterEmail: z.string().nullable().optional(),
  resolvedAt: dateOrString.nullable().optional(),
  createdAt: dateOrString,
  updatedAt: dateOrString,
}).passthrough();

export const supportTicketsContract = {
  name: "supportTickets",
  method: "GET",
  path: "/api/support/tickets",
  response: z.array(supportTicketRowSchema),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Admin security endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/admin/security/tokens — token stats table ───────────────────
// Auth: requireEmergencyAdmin (x-admin-key header; auto-passes in non-prod).
const tokenStatRowSchema = z.object({
  tokenHash: z.string(),
  tokenPrefix: z.string(),
  scope: z.string(),
  uniqueIps: z.number(),
  uniqueDevices: z.number(),
  totalRequests: z.number(),
  lastAccess: dateOrString,
  flaggedCount: z.number(),
}).passthrough();

export const adminSecurityTokensContract = {
  name: "adminSecurityTokens",
  method: "GET",
  path: "/api/admin/security/tokens",
  requiresAdmin: true,
  response: z.object({
    tokens: z.array(tokenStatRowSchema),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Client portal endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── POST /api/portal/login — request a magic login link ──────────────────
export const portalLoginContract = {
  name: "portalLogin",
  method: "POST",
  path: "/api/portal/login",
  body: z.object({ email: z.string().email() }),
  response: z.object({
    success: z.literal(true),
    message: z.string(),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/verify/:token — redeem a magic login token ───────────
export const portalVerifyContract = {
  name: "portalVerify",
  method: "GET",
  path: "/api/portal/verify/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z.object({
    success: z.literal(true),
    token: z.string(),
    email: z.string(),
    expiresAt: z.string(),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/me — authenticated session: list client bookings ──────
const portalBookingSummarySchema = z.object({
  id: z.number(),
  referenceCode: z.string().nullable(),
  artistSlug: z.string().nullable(),
  artistName: z.string(),
  date: z.string(),
  time: z.string().nullable(),
  serviceType: z.string().nullable(),
  status: z.string(),
  artistConfirmed: z.string(),
  depositStatus: z.string(),
  depositAmount: z.number().nullable(),
  showStatus: z.string().nullable(),
  releaseFormLink: z.string().nullable(),
  rescheduleLink: z.string().nullable(),
  npsLink: z.string().nullable(),
  depositLink: z.string().nullable(),
  aftercareSentAt: dateOrString.nullable().optional(),
  completedAt: dateOrString.nullable().optional(),
  createdAt: dateOrString.nullable().optional(),
}).passthrough();

export const portalMeContract = {
  name: "portalMe",
  method: "GET",
  path: "/api/portal/me",
  response: z.object({
    email: z.string(),
    bookings: z.array(portalBookingSummarySchema),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/appointment/:referenceCode — appointment detail ───────
export const portalAppointmentContract = {
  name: "portalAppointment",
  method: "GET",
  path: "/api/portal/appointment/:referenceCode",
  params: z.object({ referenceCode: z.string().min(1) }),
  response: z.object({
    id: z.number(),
    referenceCode: z.string().nullable(),
    artistSlug: z.string().nullable(),
    artistName: z.string(),
    date: z.string(),
    time: z.string().nullable(),
    serviceType: z.string().nullable(),
    description: z.string().nullable(),
    status: z.string(),
    artistConfirmed: z.string(),
    depositStatus: z.string(),
    depositAmount: z.number().nullable(),
    depositPaidAt: dateOrString.nullable().optional(),
    showStatus: z.string().nullable(),
    placement: z.string().nullable().optional(),
    tattooSize: z.string().nullable().optional(),
    estimatedCostMin: z.number().nullable().optional(),
    estimatedCostMax: z.number().nullable().optional(),
    referenceImageUrls: z.array(z.string()).nullable().optional(),
    afterPhotoUrls: z.array(z.string()),
    releaseFormStatus: z.string().nullable().optional(),
    releaseFormLink: z.string().nullable(),
    rescheduleLink: z.string().nullable(),
    npsLink: z.string().nullable(),
    depositLink: z.string().nullable(),
  }).passthrough(),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Token-gated booking action endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/reschedule/:token — load booking for rescheduling ────────────
export const rescheduleBookingContract = {
  name: "rescheduleBooking",
  method: "GET",
  path: "/api/reschedule/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    artistSlug: z.string().nullable(),
    artistName: z.string(),
    date: z.string(),
    time: z.string().nullable(),
    serviceType: z.string().nullable(),
    referenceCode: z.string().nullable(),
    description: z.string().nullable(),
    tattooSize: z.string().nullable().optional(),
    placement: z.string().nullable().optional(),
    estimatedCostMin: z.number().nullable().optional(),
    estimatedCostMax: z.number().nullable().optional(),
    referenceImageUrls: z.array(z.string()).nullable().optional(),
  }).passthrough(),
} as const satisfies ApiContract;

// ── GET /api/admin/action/:token — manage booking magic link ─────────────
const adminActionBookingSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  artistSlug: z.string().nullable(),
  artistName: z.string(),
  date: z.string(),
  time: z.string().nullable(),
  serviceType: z.string().nullable(),
  description: z.string().nullable(),
  referenceCode: z.string().nullable(),
  artistConfirmed: z.string(),
  depositStatus: z.string(),
  depositAmount: z.number().nullable(),
  showStatus: z.string().nullable(),
  status: z.string().optional(),
  afterPhotoUrls: z.array(z.string()),
}).passthrough();

export const adminActionTokenContract = {
  name: "adminActionToken",
  method: "GET",
  path: "/api/admin/action/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z.object({
    booking: adminActionBookingSchema,
    action: z.string(),
    actionLabel: z.string(),
    availableActions: z.array(z.unknown()),
    expiresAt: z.string(),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//          Client portal mutation endpoints (portal-session gated)
// ─────────────────────────────────────────────────────────────────────────

// ── PATCH /api/portal/appointment/:referenceCode — client updates tattoo details ──
// Auth: requirePortalAuth (portal session cookie/header).
// The client can patch their own booking's description, placement, size, or
// color before the appointment date.  The response is always { message }.
export const portalAppointmentUpdateContract = {
  name: "portalAppointmentUpdate",
  method: "PATCH",
  path: "/api/portal/appointment/:referenceCode",
  params: z.object({ referenceCode: z.string().min(1) }),
  body: z.object({
    description: z.string().nullable().optional(),
    placement: z.string().nullable().optional(),
    tattooSize: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
  }).optional(),
  response: z.object({ message: z.string() }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//          Client portal AI feature endpoints (Phase 1)
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/portal/tier-status — session count + spend tier tracker ──────
const portalTierSchema = z.object({
  id: z.string(),
  label: z.string(),
  benefit: z.string(),
});

export const portalTierStatusContract = {
  name: "portalTierStatus",
  method: "GET",
  path: "/api/portal/tier-status",
  response: z.object({
    sessionCount: z.number(),
    totalSpend: z.number(),
    progressToNext: z.number(),
    currentTier: portalTierSchema,
    nextTier: z.object({
      id: z.string(),
      label: z.string(),
      sessionsNeeded: z.number(),
    }).nullable(),
    allTiers: z.array(portalTierSchema.extend({ minSessions: z.number() })),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/anniversaries — upcoming tattoo anniversaries ─────────
export const portalAnniversariesContract = {
  name: "portalAnniversaries",
  method: "GET",
  path: "/api/portal/anniversaries",
  response: z.object({
    anniversaries: z.array(z.object({
      referenceCode: z.string(),
      description: z.string().nullable(),
      artistName: z.string(),
      originalDate: z.string(),
      yearsAgo: z.number(),
      daysUntil: z.number(),
    })),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/referral — get or create client referral link ─────────
export const portalReferralContract = {
  name: "portalReferral",
  method: "GET",
  path: "/api/portal/referral",
  response: z.object({
    referralToken: z.string(),
    referralLink: z.string(),
    referralCount: z.number(),
  }),
} as const satisfies ApiContract;

// ── POST /api/portal/budget-estimate — AI budget planning wizard ──────────
export const portalBudgetEstimateContract = {
  name: "portalBudgetEstimate",
  method: "POST",
  path: "/api/portal/budget-estimate",
  body: z.object({
    style: z.string().optional(),
    size: z.string().optional(),
    placement: z.string().optional(),
    complexity: z.string().optional(),
    colorPreference: z.string().optional(),
    additionalDetails: z.string().optional(),
  }).optional(),
  response: z.object({
    estimateLow: z.number(),
    estimateHigh: z.number(),
    sessionCount: z.string(),
    rationale: z.string(),
    cta: z.string(),
  }),
} as const satisfies ApiContract;

// ── POST /api/portal/appointments/:referenceCode/brief — pre-session brief ─
export const portalAppointmentBriefContract = {
  name: "portalAppointmentBrief",
  method: "POST",
  path: "/api/portal/appointments/:referenceCode/brief",
  params: z.object({ referenceCode: z.string().min(1) }),
  body: z.object({
    mood: z.string().optional(),
    size: z.string().optional(),
    placement: z.string().optional(),
    inspirations: z.string().optional(),
    avoidList: z.string().optional(),
    additionalNotes: z.string().optional(),
  }).optional(),
  response: z.object({
    brief: z.string(),
    bookingReferenceCode: z.string(),
    artistName: z.string(),
  }),
} as const satisfies ApiContract;

// ── POST /api/portal/appointments/:referenceCode/share-card-caption ────────
export const portalShareCardCaptionContract = {
  name: "portalShareCardCaption",
  method: "POST",
  path: "/api/portal/appointments/:referenceCode/share-card-caption",
  params: z.object({ referenceCode: z.string().min(1) }),
  body: z.object({ clientNote: z.string().optional() }).optional(),
  response: z.object({
    caption: z.string(),
    artistName: z.string(),
    studioName: z.string(),
  }),
} as const satisfies ApiContract;

// ── GET /api/portal/appointments/:referenceCode/aftercare-status ──────────
const aftercareCheckInSchema = z.object({
  day: z.number(),
  label: z.string(),
  tip: z.string(),
  sent: z.boolean(),
  reached: z.boolean(),
  current: z.boolean(),
});

export const portalAftercareStatusContract = {
  name: "portalAftercareStatus",
  method: "GET",
  path: "/api/portal/appointments/:referenceCode/aftercare-status",
  params: z.object({ referenceCode: z.string().min(1) }),
  response: z.union([
    z.object({ active: z.literal(false), reason: z.string() }),
    z.object({
      active: z.literal(true),
      daysSinceCompletion: z.number(),
      completedAt: z.string(),
      checkIns: z.array(aftercareCheckInSchema),
    }),
  ]),
} as const satisfies ApiContract;

// ── PATCH /api/portal/portfolio/:referenceCode/story — update story note ───
export const portalPortfolioStoryContract = {
  name: "portalPortfolioStory",
  method: "PATCH",
  path: "/api/portal/portfolio/:referenceCode/story",
  params: z.object({ referenceCode: z.string().min(1) }),
  body: z.object({
    storyNote: z.string().nullable().optional(),
    storyPrivate: z.boolean().optional(),
  }).optional(),
  response: z.object({
    ok: z.literal(true),
    storyNote: z.string().nullable(),
    storyPrivate: z.boolean(),
  }),
} as const satisfies ApiContract;

// ── POST /api/portal/style-profile/from-quiz — save quiz-derived styles ───
export const portalStyleFromQuizContract = {
  name: "portalStyleFromQuiz",
  method: "POST",
  path: "/api/portal/style-profile/from-quiz",
  body: z.object({
    topStyles: z.array(z.string().max(50)).min(1).max(6),
  }),
  response: z.object({
    ok: z.boolean(),
    skipped: z.boolean().optional(),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//          Healed-photo submission (token-gated, multipart upload)
// ─────────────────────────────────────────────────────────────────────────

// ── POST /api/healed-photo/:bookingId/:token — submit a healed photo ──────
// Auth: bookingId + healedPhotoToken path params (token.length >= 32).
// Body: multipart/form-data with a `photo` file field and optional `note`.
// Response on success: { success: true, id: number }.
// Contract tests accept 400/404/503 — without a real multipart file the
// route returns 400; without a valid booking token the storage mock returns
// null which yields 404; the kill-switch returns 503.
export const healedPhotoSubmitContract = {
  name: "healedPhotoSubmit",
  method: "POST",
  path: "/api/healed-photo/:bookingId/:token",
  params: z.object({
    bookingId: z.union([z.string(), z.number()]),
    token: z.string().min(1),
  }),
  response: z.object({ success: z.literal(true), id: z.number() }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//          Release-form submission (token-gated, public client-facing)
// ─────────────────────────────────────────────────────────────────────────

// ── POST /api/release-forms/:token/submit — submit a signed release form ──
// Auth: release-form token in path (validated against release_form_submissions).
// Body: client identity + health answers + signature fields (large object).
// Response on success: { success: true, submittedAt: string | Date }.
export const releaseFormSubmitContract = {
  name: "releaseFormSubmit",
  method: "POST",
  path: "/api/release-forms/:token/submit",
  params: z.object({ token: z.string().min(1) }),
  body: z.object({
    clientName: z.string().optional(),
    clientEmail: z.string().optional(),
    clientPhone: z.string().nullable().optional(),
    clientDob: z.string().optional(),
    clientSignature: z.string().optional(),
  }).passthrough().optional(),
  response: z.object({
    success: z.literal(true),
    submittedAt: dateOrString.nullable().optional(),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Public waitlist endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── POST /api/waitlist — join the waitlist for an artist ──────────────────
// Public. Body: insertBookingWaitlistSchema fields. On success returns either
// { success: true, id: number } (new entry) or { success: true, alreadyOnList: true }
// (duplicate). Frontend (BookingCalendar, WalkinBooking, etc.) reads success/id.
export const waitlistJoinContract = {
  name: "waitlistJoin",
  method: "POST",
  path: "/api/waitlist",
  body: z.object({
    artistSlug: z.string(),
    clientName: z.string(),
    clientEmail: z.string().email(),
    serviceType: z.string().nullable().optional(),
    requestedDate: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  }),
  response: z.union([
    z.object({ success: z.literal(true), id: z.number() }),
    z.object({ success: z.literal(true), alreadyOnList: z.literal(true) }),
  ]),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Booking draft endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/booking-draft/resume/:token — resume a booking in progress ──
// Client-facing; no auth required. Returns the saved draft state or 404/410.
export const bookingDraftResumeContract = {
  name: "bookingDraftResume",
  method: "GET",
  path: "/api/booking-draft/resume/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z.object({
    success: z.literal(true),
    draft: z.object({
      token: z.string(),
      lastStep: z.number().nullable(),
      state: z.unknown(),
    }),
  }),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Admin waitlist endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/admin/waitlist — owner waitlist list ─────────────────────────
// Auth: requireAdmin. Returns all waitlist entries (optionally filtered by
// artist slug via ?artist= query param).
const waitlistEntrySchema = z.object({
  id: z.number(),
  artistSlug: z.string(),
  clientName: z.string(),
  clientEmail: z.string(),
  phone: z.string().nullable().optional(),
  serviceType: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  notifiedAt: dateOrString.nullable().optional(),
  createdAt: dateOrString,
}).passthrough();

export const adminWaitlistContract = {
  name: "adminWaitlist",
  method: "GET",
  path: "/api/admin/waitlist",
  requiresAdmin: true,
  response: z.array(waitlistEntrySchema),
} as const satisfies ApiContract;

// ─────────────────────────────────────────────────────────────────────────
//                       Waitlist claim endpoints
// ─────────────────────────────────────────────────────────────────────────

// ── GET /api/waitlist/claim/:token — check status of a claim link ─────────
// Public. Returned to the client when they click a "spot opened" email link.
export const waitlistClaimStatusContract = {
  name: "waitlistClaimStatus",
  method: "GET",
  path: "/api/waitlist/claim/:token",
  params: z.object({ token: z.string().min(1) }),
  response: z.object({
    valid: z.literal(true),
    expired: z.boolean(),
    claimed: z.boolean(),
    claimedByOther: z.boolean(),
    artistName: z.string(),
    artistSlug: z.string(),
    bookingDate: z.string(),
    bookingTime: z.string().nullable(),
    bookingUrl: z.string(),
    clientName: z.string().nullable(),
    clientEmail: z.string().nullable(),
    serviceType: z.string().nullable(),
  }),
} as const satisfies ApiContract;

// ── Registry ──────────────────────────────────────────────────────────────
export const apiContracts = {
  bookingsByArtist: bookingsByArtistContract,
  bookedTimesForDate: bookedTimesForDateContract,
  bookingCreate: bookingCreateContract,
  healingJourneyCreate: healingJourneyCreateContract,
  nguLeadsList: nguLeadsListContract,
  adminValidateToken: adminValidateTokenContract,
  // Public reads
  systemFlags: systemFlagsContract,
  buildInfo: buildInfoContract,
  featureFlagsEnabled: featureFlagsEnabledContract,
  featureFlagsMeta: featureFlagsMetaContract,
  piercingPricing: piercingPricingContract,
  healthCheck: healthCheckContract,
  webManifest: webManifestContract,
  artistsPublic: artistsPublicContract,
  artistsAll: artistsAllContract,
  portfolioImages: portfolioImagesContract,
  siteConfig: siteConfigContract,
  studioConfig: studioConfigContract,
  artistAvailability: artistAvailabilityContract,
  eventsPublic: eventsPublicContract,
  eventsUpcoming: eventsUpcomingContract,
  flashDesignsAvailable: flashDesignsAvailableContract,
  nguPublicConfig: nguPublicConfigContract,
  nguTrackingConfig: nguTrackingConfigContract,
  // Admin reads
  adminJobsStatus: adminJobsStatusContract,
  adminAuditLogs: adminAuditLogsContract,
  adminChangelog: adminChangelogContract,
  adminDeletedBookings: adminDeletedBookingsContract,
  featureFlagsList: featureFlagsListContract,
  featureTiersList: featureTiersListContract,
  clientDeploymentsList: clientDeploymentsListContract,
  adminActivityUnread: adminActivityUnreadContract,
  adminActivityFeed: adminActivityFeedContract,
  // Admin booking / contact / deposit reads
  adminAllBookings: adminAllBookingsContract,
  adminUpcomingBookings: adminUpcomingBookingsContract,
  adminContactSubmissions: adminContactSubmissionsContract,
  adminContacts: adminContactsContract,
  adminDeposits: adminDepositsContract,
  adminDepositsAttention: adminDepositsAttentionContract,
  adminBlockedEmails: adminBlockedEmailsContract,
  bookingEditDetails: bookingEditDetailsContract,
  // Admin mutations
  bookingRestore: bookingRestoreContract,
  piercingPricingCreate: piercingPricingCreateContract,
  piercingPricingUpdate: piercingPricingUpdateContract,
  piercingPricingDelete: piercingPricingDeleteContract,
  // Artist portal (token-gated)
  artistPortalInfo: artistPortalInfoContract,
  artistPortalBookings: artistPortalBookingsContract,
  artistPortalClients: artistPortalClientsContract,
  artistPortalEarnings: artistPortalEarningsContract,
  artistPortalWalkins: artistPortalWalkinsContract,
  artistPortalFlash: artistPortalFlashContract,
  artistPortalWaitlist: artistPortalWaitlistContract,
  artistPortalDeposits: artistPortalDepositsContract,
  artistPortalForms: artistPortalFormsContract,
  artistPortalHealing: artistPortalHealingContract,
  artistPortalStats: artistPortalStatsContract,
  artistPortalReviews: artistPortalReviewsContract,
  artistPortalTattooWorks: artistPortalTattooWorksContract,
  artistPortalSupportTickets: artistPortalSupportTicketsContract,
  artistPortalAvailability: artistPortalAvailabilityContract,
  artistPortalProfileData: artistPortalProfileDataContract,
  artistPortalFlashDelete: artistPortalFlashDeleteContract,
  artistPortalFlashReorder: artistPortalFlashReorderContract,
  artistPortalWaitlistNotify: artistPortalWaitlistNotifyContract,
  artistPortalIssueExchangeCode: artistPortalIssueExchangeCodeContract,
  // Staff (receptionist) portal (token-gated)
  staffPortalMe: staffPortalMeContract,
  staffPortalUpcomingBookings: staffPortalUpcomingBookingsContract,
  staffPortalWalkinQueue: staffPortalWalkinQueueContract,
  staffPortalMessagesSummary: staffPortalMessagesSummaryContract,
  staffPortalMessageThread: staffPortalMessageThreadContract,
  staffPortalMessageSend: staffPortalMessageSendContract,
  staffPortalSupportTickets: staffPortalSupportTicketsContract,
  staffPortalTrainingProgress: staffPortalTrainingProgressContract,
  staffPortalContacts: staffPortalContactsContract,
  staffPortalNotificationsFeed: staffPortalNotificationsFeedContract,
  staffPortalArrivalStatus: staffPortalArrivalStatusContract,
  staffPortalWalkinUpdate: staffPortalWalkinUpdateContract,
  // Manage Studio (admin/studio token-gated)
  adminStudioConfig: adminStudioConfigContract,
  adminStudioCommTemplates: adminStudioCommTemplatesContract,
  adminStudioCommTemplateDelete: adminStudioCommTemplateDeleteContract,
  // Support portal
  supportTickets: supportTicketsContract,
  // Admin security
  adminSecurityTokens: adminSecurityTokensContract,
  // Client portal
  portalLogin: portalLoginContract,
  portalVerify: portalVerifyContract,
  portalMe: portalMeContract,
  portalAppointment: portalAppointmentContract,
  portalAppointmentUpdate: portalAppointmentUpdateContract,
  // Client portal AI features (Phase 1) + quiz integration
  portalStyleFromQuiz: portalStyleFromQuizContract,
  portalTierStatus: portalTierStatusContract,
  portalAnniversaries: portalAnniversariesContract,
  portalReferral: portalReferralContract,
  portalBudgetEstimate: portalBudgetEstimateContract,
  portalAppointmentBrief: portalAppointmentBriefContract,
  portalShareCardCaption: portalShareCardCaptionContract,
  portalAftercareStatus: portalAftercareStatusContract,
  portalPortfolioStory: portalPortfolioStoryContract,
  // Token-gated upload/submission routes
  healedPhotoSubmit: healedPhotoSubmitContract,
  releaseFormSubmit: releaseFormSubmitContract,
  // Token-gated booking actions
  rescheduleBooking: rescheduleBookingContract,
  adminActionToken: adminActionTokenContract,
  // Public waitlist join
  waitlistJoin: waitlistJoinContract,
  // Booking draft recovery
  bookingDraftResume: bookingDraftResumeContract,
  // Admin waitlist
  adminWaitlist: adminWaitlistContract,
  // Public waitlist claim status
  waitlistClaimStatus: waitlistClaimStatusContract,
} as const;

export type ApiContractKey = keyof typeof apiContracts;
export type ResponseOf<C extends ApiContract> = z.infer<C["response"]>;

/**
 * Replace `:name` placeholders in a path template with the supplied params,
 * URL-encoding each value. Throws if a required placeholder is missing.
 */
export function buildPath(
  template: string,
  params?: Record<string, string | number>,
): string {
  return template.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_match, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === null || v === "") {
      throw new Error(`Missing path parameter "${name}" for "${template}"`);
    }
    return encodeURIComponent(String(v));
  });
}
